import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ExternalLink, Mail, Ship, Users, Sparkles, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { useSite } from "../context/AppContext";
import PromotionCard from "./PromotionCard";
import ReviewsCarousel from "./ReviewsCarousel";

const MARQUEE_ITEMS = ["CRUISES", "GROUP TRIPS", "FAMILY REUNIONS", "GIRLS GETAWAYS", "MILESTONE CELEBRATIONS", "DREAM", "EXPLORE", "BOOK", "REPEAT"];

function ExtOrLink({ cta, className, testid, children }) {
  const isExternal = /^https?:/.test(cta?.url || "");
  const label = children || cta?.label;
  if (!cta?.url) return null;
  if (isExternal) {
    return (
      <a href={cta.url} {...(cta.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})} data-testid={testid} className={className}>
        {label}
        {cta.newTab && <ExternalLink className="w-4 h-4" aria-hidden="true" />}
        {cta.newTab && <span className="sr-only">(opens an external website in a new tab)</span>}
      </a>
    );
  }
  return <Link to={cta.url} data-testid={testid} className={className}>{label}</Link>;
}

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

function HeroSection({ data }) {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 140]);
  const lines = (data.heading || "").split("\n").filter(Boolean);

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#061325]" data-testid="hero-section">
      {data.image?.url && (
        <motion.img
          src={data.image.url}
          alt={data.image.alt || ""}
          style={{ y, objectPosition: `${data.image.focal?.x ?? 50}% ${data.image.focal?.y ?? 50}%` }}
          className="absolute inset-0 w-full h-[115%] object-cover animate-kenburns"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-[#061325]/95 via-[#061325]/70 to-[#061325]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#061325] via-transparent to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-28 w-full">
        {data.badge && (
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-block text-[11px] sm:text-xs uppercase tracking-[0.3em] text-amber-300 border border-amber-500/35 rounded-full px-4 py-2 mb-8 bg-[#061325]/40 backdrop-blur"
            data-testid="hero-badge">
            {data.badge}
          </motion.p>
        )}
        <h1 className="font-heading h-display font-medium text-amber-50 leading-[1.06] tracking-tight max-w-3xl" data-testid="hero-headline-title">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        {data.subtitle && (
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 text-base sm:text-lg text-slate-200/90 max-w-xl leading-relaxed" data-testid="hero-subtitle-description">
            {data.subtitle}
          </motion.p>
        )}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }} className="mt-9 flex flex-wrap gap-4">
          {(data.ctas || []).map((cta, i) => (
            <ExtOrLink key={i} cta={cta} testid={`hero-cta-${i}`} className={cta.style === "outline" ? "btn-outline text-amber-100" : "btn-brand"} />
          ))}
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute bottom-24 left-1/2 -translate-x-1/2 text-amber-200/60 hidden md:block" aria-hidden="true">
        <ChevronDown className="w-6 h-6 animate-bounce" />
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 border-t border-amber-500/15 bg-[#061325]/80 backdrop-blur overflow-hidden py-3.5" aria-hidden="true">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center shrink-0">
              {MARQUEE_ITEMS.map((item, i) => (
                <span key={`${copy}-${i}`} className="flex items-center text-[12px] tracking-[0.35em] text-amber-200/70 font-medium">
                  <span className="px-6">{item}</span>
                  <span className="text-amber-500/70 text-[9px]">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BannerSection({ data }) {
  return (
    <section className="relative py-24 sm:py-32 flex items-center overflow-hidden bg-[#061325]">
      {data.image?.url && (
        <img src={data.image.url} alt={data.image.alt || ""} className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: `${data.image.focal?.x ?? 50}% ${data.image.focal?.y ?? 50}%` }} />
      )}
      <div className="absolute inset-0 bg-[#061325]/80" />
      <motion.div {...fadeUp} className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-amber-50 font-medium" data-testid="banner-heading">{data.heading}</h1>
        {data.text && <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-2xl">{data.text}</p>}
        <div className="gold-divider mt-6" />
      </motion.div>
    </section>
  );
}

function ImageTextSection({ data, flip }) {
  const imgLeft = data.imagePosition === "left";
  return (
    <section className={`section-pad ${flip ? "bg-[#F4EFE6]" : ""}`}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 grid gap-10 lg:gap-16 items-center lg:grid-cols-2`}>
        <motion.div {...fadeUp} className={imgLeft ? "lg:order-1" : "lg:order-2"}>
          {data.image?.url && (
            <div className="relative">
              <div className="absolute -inset-3 border border-amber-500/30 rounded-3xl pointer-events-none" aria-hidden="true" />
              <img src={data.image.url} alt={data.image.alt || ""} loading="lazy"
                className="rounded-2xl w-full h-[320px] sm:h-[420px] object-cover shadow-[0_24px_60px_rgba(11,29,58,0.18)]"
                style={{ objectPosition: `${data.image.focal?.x ?? 50}% ${data.image.focal?.y ?? 50}%` }} />
            </div>
          )}
        </motion.div>
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }} className={imgLeft ? "lg:order-2" : "lg:order-1"}>
          {data.heading && <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.9rem] leading-tight text-[#0B1D3A] font-semibold">{data.heading}</h2>}
          <div className="gold-divider mt-5 mb-6" />
          {data.body && data.body.split("\n").filter(Boolean).map((p, i) => (
            <p key={i} className="text-base leading-relaxed text-slate-600 mb-4">{p}</p>
          ))}
          {data.cta?.url && (
            <ExtOrLink cta={data.cta} testid="section-cta-button" className="btn-brand mt-4" />
          )}
        </motion.div>
      </div>
    </section>
  );
}

function RichTextSection({ data }) {
  return (
    <section className="section-pad">
      <motion.div {...fadeUp} className="max-w-3xl mx-auto px-4 sm:px-6">
        {data.heading && <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1D3A] font-semibold">{data.heading}</h2>}
        <div className="gold-divider mt-5 mb-6" />
        {(data.body || "").split("\n").filter(Boolean).map((p, i) => (
          <p key={i} className="text-base sm:text-lg leading-relaxed text-slate-600 mb-4">{p}</p>
        ))}
      </motion.div>
    </section>
  );
}

function CtaSection({ data }) {
  const { site } = useSite();
  const fee = site?.settings?.consultationFee;
  return (
    <section className="section-pad bg-[#0B1D3A] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 85% 20%, #D4AF37 0, transparent 45%)" }} />
      <motion.div {...fadeUp} className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-amber-50 font-medium">{data.heading}</h2>
        {data.text && <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">{data.text}</p>}
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          {data.button?.url && <ExtOrLink cta={data.button} testid="cta-section-button" className="btn-brand" />}
          {data.secondaryButton?.url && <ExtOrLink cta={data.secondaryButton} testid="cta-section-secondary-button" className="btn-outline text-amber-100" />}
        </div>
        {data.showFee && fee && (
          <p data-testid="cta-fee-notice" className="mt-6 text-amber-300/90 text-sm font-medium">{fee}</p>
        )}
      </motion.div>
    </section>
  );
}

function GallerySection({ data }) {
  const images = data.images || [];
  if (!images.length) return null;
  return (
    <section className="section-pad">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {data.heading && (
          <motion.div {...fadeUp} className="mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1D3A] font-semibold">{data.heading}</h2>
            <div className="gold-divider mt-5" />
          </motion.div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((img, i) => (
            <motion.img key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              src={img.url} alt={img.alt || ""} loading="lazy"
              className={`rounded-2xl object-cover w-full ${i === 0 ? "col-span-2 h-72 sm:h-96" : "h-52 sm:h-64"}`}
              style={{ objectPosition: `${img.focal?.x ?? 50}% ${img.focal?.y ?? 50}%` }} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PromotionsSection({ data }) {
  const [promos, setPromos] = useState(null);
  useEffect(() => {
    const q = data.source === "featured" ? "?featured=1" : "";
    api.get(`/public/promotions${q}`).then(({ data }) => setPromos(data)).catch(() => setPromos([]));
  }, [data.source]);

  return (
    <section className="section-pad" data-testid="promotions-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4" aria-hidden="true" /> Offers</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0B1D3A] font-semibold">{data.heading || "Current Promotions"}</h2>
            <div className="gold-divider mt-5" />
          </div>
          {data.source === "featured" && (
            <Link to="/promotions" data-testid="promotions-view-all-link" className="btn-outline !text-[#0B1D3A] !py-2.5 !px-5 text-sm">View All Promotions</Link>
          )}
        </motion.div>
        {promos === null ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[0, 1, 2].map((i) => <div key={i} className="h-96 rounded-2xl bg-slate-200/60 animate-pulse" />)}</div>
        ) : promos.length === 0 ? (
          <div className="card-luxe p-10 text-center" data-testid="promotions-empty-state">
            <Ship className="w-10 h-10 text-amber-600 mx-auto mb-4" aria-hidden="true" />
            <p className="text-lg text-slate-600 max-w-xl mx-auto">New travel offers are coming soon. Explore available options or contact Lisa to plan your next trip.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <ExtOrLink cta={{ label: "Explore Travel Options", url: "https://www.evotravelagent.com/ldixontravel", newTab: true }} testid="promotions-empty-explore" className="btn-brand" />
              <Link to="/contact" data-testid="promotions-empty-contact" className="btn-outline !text-[#0B1D3A]">Contact Lisa</Link>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {promos.map((p, i) => (
              <motion.div key={p.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}>
                <PromotionCard promo={p} index={i} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FaqSection({ data }) {
  const items = data.items || [];
  return (
    <section className="section-pad">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {data.heading && (
          <motion.div {...fadeUp} className="mb-8">
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1D3A] font-semibold">{data.heading}</h2>
            <div className="gold-divider mt-5" />
          </motion.div>
        )}
        <div className="space-y-4">
          {items.map((f, i) => (
            <motion.details key={i} {...fadeUp} className="card-luxe p-5 group" data-testid={`faq-item-${i}`}>
              <summary className="cursor-pointer font-semibold text-[#0B1D3A] text-base list-none flex justify-between items-center gap-4">
                {f.question}
                <ChevronDown className="w-5 h-5 text-amber-600 transition-transform group-open:rotate-180 shrink-0" aria-hidden="true" />
              </summary>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">{f.answer}</p>
            </motion.details>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactInfoSection({ data }) {
  const { site } = useSite();
  const c = site?.contact || {};
  return (
    <section className="section-pad !pb-0">
      <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6">
        <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1D3A] font-semibold">{data.heading || "Get in Touch"}</h2>
        <div className="gold-divider mt-5 mb-8" />
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="card-luxe p-6">
            <p className="eyebrow mb-2">Your Advisor</p>
            <p className="font-heading text-2xl text-[#0B1D3A] font-semibold" data-testid="contact-advisor-name">{c.advisorName}</p>
            <p className="text-slate-500 text-sm mt-1">{c.title}</p>
            {c.registration && <p className="text-slate-500 text-sm mt-2" data-testid="contact-registration">Florida registration number: {c.registration}</p>}
          </div>
          <div className="card-luxe p-6">
            <p className="eyebrow mb-2">Email</p>
            <a href={`mailto:${c.email}`} data-testid="contact-email-link" className="inline-flex items-center gap-2 text-amber-700 font-semibold hover:text-amber-800 break-all">
              <Mail className="w-4 h-4 shrink-0" aria-hidden="true" /> {c.email}
            </a>
            {c.phone && <p className="text-slate-600 text-sm mt-3" data-testid="contact-phone">{c.phone}</p>}
            {c.address && <p className="text-slate-600 text-sm mt-2 whitespace-pre-line" data-testid="contact-address">{c.address}</p>}
          </div>
        </div>
      </motion.div>
    </section>
  );
}

export default function SectionRenderer({ sections = [] }) {
  return (
    <>
      {sections.map((s, i) => {
        const key = s.id || i;
        switch (s.type) {
          case "hero": return <HeroSection key={key} data={s.data} />;
          case "banner": return <BannerSection key={key} data={s.data} />;
          case "image_text": return <ImageTextSection key={key} data={s.data} flip={i % 2 === 1} />;
          case "rich_text": return <RichTextSection key={key} data={s.data} />;
          case "cta": return <CtaSection key={key} data={s.data} />;
          case "gallery": return <GallerySection key={key} data={s.data} />;
          case "promotions": return <PromotionsSection key={key} data={s.data} />;
          case "faq": return <FaqSection key={key} data={s.data} />;
          case "reviews": return <ReviewsCarousel key={key} heading={s.data.heading} items={s.data.items} />;
          case "contact_info": return <ContactInfoSection key={key} data={s.data} />;
          default: return null;
        }
      })}
    </>
  );
}
